import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Directive, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Select, Store } from "@ngxs/store";
import { IFileUploadObject } from "src/app/shared/components/file-uploader/file-upload-object.interface";
import { OrdersService } from "../components/receive-edi/orders.service";
import { ISaveAttachedFileEvent } from "src/app/shared/interfaces/save-attached-file-event.interface";
import { PinService } from "src/app/shared/services/pin.service";
import { OrdersState } from "src/app/shared/states/orders/orders.state";
import { merge, Observable } from "rxjs";
import { RowType } from "src/app/shared/interfaces/raw-type.enum";
import { GenericTableColumn } from "src/app/shared/interfaces/genetic-table-column";
import { FieldType } from "src/app/shared/interfaces/field-type.enum";
import { PharmacyState } from "src/app/shared/states/pharmacy/pharmacy.state";
import { IOrderSaleReturnDestructionOrderLine } from "../interfaces/order-sale-return-destruction-line-item.interface";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FileService } from "src/app/shared/services/file-service";
import { PdfSignatureService } from "src/app/shared/services/pdf-signature-service";
import { IUserName } from "src/app/shared/interfaces/user-name.interface";
import { FormControl, FormGroup } from "@angular/forms";

@UntilDestroy()
@Directive({
    selector: '[appOrderDetails]',
  })
export class OrderDetailsDirective implements OnInit, OnDestroy {
  @Select(OrdersState.orderFiles) orderFiles$: Observable<IFileUploadObject[]>;

  navigationState: any;
  saveAttachmentPinChannelName = 'saveAttachment';
  updatedAttachments: IFileUploadObject[] = [];
  isEditMode: boolean;

  columns: GenericTableColumn<IOrderSaleReturnDestructionOrderLine>[] = [];

  @ViewChild('fileAttachment') fileAttachment;
  fileAttachmentHeight: number = 0;
  @ViewChild('buyerSupplier') buyerSupplier;
  buyerSupplierHeight: number;
  
  signature: string;
  
  form: FormGroup;

  /**
   *
   */
  constructor(
    protected router: Router,
    protected store: Store,
    protected orderService: OrdersService,
    protected pinService: PinService,
    protected cd: ChangeDetectorRef,
    protected fileService: FileService,
    protected pdfSignatureService: PdfSignatureService,
  ) {
    this.navigationState = router.getCurrentNavigation()?.extras?.state;
    this.form = new FormGroup({
      csos: new FormControl()
    })
    
  }
 
  ngOnInit(): void {
    this.orderFiles$
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      setTimeout(() => {
        this.fileAttachmentHeight = this.fileAttachment?.nativeElement?.offsetHeight;
        this.cd.markForCheck();
      });
    })

    merge(this.store.select(OrdersState.orderSaleOrReturnDetailsData), this.store.select(OrdersState.orderForInventoryDestructionDetailsData))
    .pipe(untilDestroyed(this))
    .subscribe(data => {
      if (data) {
        setTimeout(() => {
          this.buyerSupplierHeight = this.buyerSupplier?.nativeElement?.offsetHeight || 0;
          this.cd.markForCheck();
        });
      }
    })
  }

  downloadFile(item: IFileUploadObject): void {
    this.fileService.downloadFile(item);
  }

  saveAttachmentsList(event: ISaveAttachedFileEvent): void {
    if (event.attachments) {
      this.pinService.openPinDialog(this.saveAttachmentPinChannelName);
      this.updatedAttachments = event.attachments;
      this.isEditMode = event.isEditMode;
      this.signature = event.signature;
    }
  }

  async signAndSendUpdatedAttachmentsToApi(orderId: number, pin: number, userName?: IUserName, orderCreateDate?: string): Promise<void> {
    const originalFiles = this.store.selectSnapshot(OrdersState.orderFiles);
    if (this.signature) {
      const name = userName?.firstName + ' ' + userName?.lastName;
      await this.pdfSignatureService.sign(
        this.updatedAttachments, 
        this.signature, 
        name, 
        this.store.selectSnapshot(PharmacyState.currentPharmacy)?.name,
        new Date(orderCreateDate),
      );
    }
    this.fileService.saveAttachmentsList(
      { orderId, pin, csos: this.form.getRawValue().csos },
      this.isEditMode,
      originalFiles,
      this.updatedAttachments
    );
  }

  setUpTable(): void {
    this.columns =  [
      new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: 'Drug', field: 'friendlyName',  RowType: RowType.Double,
        doubleRowField: {
          headingField: 'friendlyName', headingFieldType: FieldType.Text, 
          secondaryField1: 'packageNdc', secondaryFieldType1: FieldType.Text
        },
      })];

      if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
        this.columns.push( new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: 'Location', field: 'locationName', sortable: false, style: {width: '20%'}}));
      }

      this.columns.push(
        new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: 'Starting', field: 'starting', sortable: false, fieldType: FieldType.Number, style: {width: '10%'}}),
        new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: 'Sold', field: 'addedQty', sortable: false, fieldType: FieldType.Number, style:{ width: '10%'}, class: 'background-yellow-50'}), 
        new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: 'On-Hand', field: 'endingQty', sortable: false, fieldType: FieldType.Number, style: {width: '10%'}}),
        new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: 'SOH', field: 'sohEndingQty', sortable: false, fieldType: FieldType.Number, style: {width: '10%'}}),
        new GenericTableColumn<IOrderSaleReturnDestructionOrderLine>({ header: '', field: '' as any, sortable: false, style: {width: '20%'}})
    );
  }

  calculateTableHeight(): string {
    return this.fileAttachmentHeight > 0 ? 
     `calc(100vh - 225px - ${this.fileAttachmentHeight + this.buyerSupplierHeight + 10}px)` :
     `calc(100vh - ${341 + this.buyerSupplierHeight}px)`;
  }

  ngOnDestroy(): void {
    this.updatedAttachments = null;
    this.signature = null;
  }
}